import { z } from 'zod';
import { IsoDate, IsoDateTime, NonEmptyName, Uuid } from '../primitives.js';

/**
 * WS-13 Feature 4 — CONSULTATION / CONSENT records (LRU-07).
 *
 * DESIGN (LOCKED, WS13-retention-design.md §Feature 4 + Locked decision 3): a PII/LIABILITY feature with NO
 * money and NO blob storage. A SIGNATURE is a TYPED-NAME ATTESTATION — a typed name + an explicit "I agree"
 * + signed_at + signed_ip + a FROZEN VERBATIM SNAPSHOT of the wording signed. The wording IS the liability
 * surface, so a per-record frozen snapshot (snapshotTitle/snapshotBody) is reproducible years later even if
 * the template is later edited.
 *
 * Templates are VERSIONED-NOT-MUTATED: editing the wording creates a NEW version (version+1) and links the
 * OLD one via supersededBy — records already signed against the old version keep their frozen snapshot, so a
 * later edit can NEVER retro-alter signed evidence. Records are APPEND-ONLY + IMMUTABLE-ONCE-SIGNED: a
 * correction is a NEW superseding row, never an edit.
 *
 * Patch-test: a `patch_test` record is the source of truth behind the booking prompt — it projects
 * clients.patch_test_last_at = signedAt and clients.patch_test_due_at = validUntil
 * (= signedAt + template.validityDays OR service.patch_test_valid_days). Those two client DATE columns are
 * kept (the booking prompt reads them) as a derived cache of the newest patch_test record.
 *
 * Drawn signatures are a schema-ready fast-follow: signatureKind discriminates 'typed'|'drawn' and
 * signatureBlobKey stays NULL until real blob storage lands (no blob infra exists today).
 */

// ── Enums ────────────────────────────────────────────────────────────────────

/**
 * The category of consent/consultation form. 'patch_test' drives the clients.patch_test_* projection +
 * validUntil; 'allergy' is a sensitive-PII declaration; 'consultation'/'waiver'/'marketing'/'other' are the
 * rest of the liability surface a salon may capture.
 */
export const ConsentKind = z.enum([
  'consultation',
  'patch_test',
  'allergy',
  'waiver',
  'marketing',
  'other',
]);
export type ConsentKind = z.infer<typeof ConsentKind>;

/**
 * How the attestation was captured. 'typed' = the locked typed-name attestation (the only active path);
 * 'drawn' = a future drawn-signature image (requires blob infra; signatureBlobKey is set only then).
 */
export const SignatureKind = z.enum(['typed', 'drawn']);
export type SignatureKind = z.infer<typeof SignatureKind>;

/**
 * The surface a record was signed through. 'console_staff' = a staff member captured it at the salon;
 * 'client_portal' = the client self-served on my.salonomy.com; 'booking' = captured in a booking flow.
 */
export const SignedVia = z.enum(['console_staff', 'client_portal', 'booking']);
export type SignedVia = z.infer<typeof SignedVia>;

// ── Template (the salon-defined form, versioned-not-mutated) ────────────────────

/**
 * A salon-defined consent/consultation form definition. `body` is the prose (the liability surface), frozen
 * into a record's snapshotBody at sign. `fields` is the form field schema (the questions/checkboxes).
 * `validityDays` overrides service.patch_test_valid_days for a patch_test/allergy window. An edit creates a
 * NEW version; `supersededBy` on the OLD row points at the new one (and `active` flips false).
 */
export const ConsentTemplate = z
  .object({
    id: Uuid,
    salonId: Uuid,
    kind: ConsentKind,
    title: z.string(),
    version: z.int().min(1),
    fields: z.unknown().describe('the form field schema (questions/checkboxes the client fills)'),
    body: z.string().describe('the prose/wording — the liability surface, frozen at sign'),
    validityDays: z
      .int()
      .min(1)
      .nullable()
      .describe('override window (days) for a patch_test/allergy template; null = no override'),
    active: z.boolean(),
    supersededBy: Uuid.nullable().describe('the newer version that replaced this one; null = live'),
    createdByStaffId: Uuid.nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'ConsentTemplate' });
export type ConsentTemplate = z.infer<typeof ConsentTemplate>;

/**
 * Create or version a consent template (manager). A POST creates v1; a PATCH that changes the WORDING
 * (`body`/`title`/`fields`/`validityDays`) creates a NEW version, never mutating a row that has signed
 * records. `active` toggles a (de)activation without a version bump.
 */
export const UpsertConsentTemplate = z
  .object({
    kind: ConsentKind.optional(),
    title: z.string().trim().min(1).max(200).optional(),
    fields: z.unknown().optional(),
    body: z.string().max(20_000).optional(),
    validityDays: z.int().min(1).max(3650).nullish(),
    active: z.boolean().optional(),
  })
  .meta({ id: 'UpsertConsentTemplate' });
export type UpsertConsentTemplate = z.infer<typeof UpsertConsentTemplate>;

// ── Record (the immutable signed evidence) ──────────────────────────────────────

/**
 * ONE immutable captured/signed instance — the durable liability evidence. snapshotTitle/snapshotBody are
 * the FROZEN copy of the template wording at sign (reproducible verbatim even if the template later
 * changes). `answers` holds the filled fields incl allergy/medical declarations (sensitive PII).
 * signatureBlobKey is null unless `drawn`. validUntil is set for a patch_test (drives the projection).
 */
export const ConsentRecord = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable(),
    clientId: Uuid.nullable().describe('null for a pre-link walk-in/booker'),
    templateId: Uuid.nullable(),
    serviceId: Uuid.nullable().describe('for a patch_test, the service it covers'),
    kind: ConsentKind,
    snapshotTitle: z.string().describe('FROZEN template title at sign'),
    snapshotBody: z.string().describe('FROZEN template wording at sign — the liability surface'),
    answers: z.unknown().describe('the filled fields incl allergy/medical declarations (PII)'),
    signatureKind: SignatureKind,
    signedName: z.string(),
    signatureBlobKey: z.string().nullable().describe('only set for a drawn signature (future)'),
    signedAt: IsoDateTime,
    signedVia: SignedVia,
    signedByStaffId: Uuid.nullable(),
    signedIp: z.string().nullable(),
    validUntil: IsoDate.nullable().describe('for a patch_test: signedAt + the validity window'),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'ConsentRecord' });
export type ConsentRecord = z.infer<typeof ConsentRecord>;

/**
 * Capture a signed consent record (staff). The server FREEZES snapshotTitle/snapshotBody from the template
 * at sign time, sets signedVia='console_staff', signedByStaffId + signedIp from the request, signedAt=now.
 * For kind='patch_test' it sets validUntil = signedAt + (template.validityDays OR service.patch_test_valid_days)
 * and PROJECTS clients.patch_test_last_at/due_at. A correction is a NEW record, never an edit.
 */
export const CreateConsentRecord = z
  .object({
    templateId: Uuid.optional().describe(
      'the template to freeze a snapshot from; omitted = ad-hoc',
    ),
    kind: ConsentKind,
    serviceId: Uuid.optional().describe('the service a patch_test covers (drives validUntil)'),
    answers: z.unknown().optional().describe('the filled fields incl allergy/medical declarations'),
    signedName: NonEmptyName.describe('the typed-name attestation'),
    signatureKind: SignatureKind.default('typed'),
  })
  .meta({ id: 'CreateConsentRecord' });
export type CreateConsentRecord = z.infer<typeof CreateConsentRecord>;

// ── Client portal DTOs (NO internal ids — customer-facing only) ─────────────────

/**
 * The client-portal view of ONE of the client's own signed records. Customer-facing ONLY: NO clientId /
 * templateId / renterId / staffId / record id — the snapshot title/body, kind, signedAt, validUntil, and a
 * derived status (whether a dated record like a patch test is still valid).
 */
export const ClientConsentView = z
  .object({
    salonName: z.string(),
    kind: ConsentKind,
    title: z.string().describe('the FROZEN snapshot title'),
    body: z.string().describe('the FROZEN snapshot wording (reproducible verbatim)'),
    signedName: z.string(),
    signedAt: IsoDateTime,
    validUntil: IsoDate.nullable(),
    /** 'valid' (no expiry, or validUntil in the future), 'expired' (validUntil in the past). */
    status: z.enum(['valid', 'expired']),
  })
  .meta({ id: 'ClientConsentView' });
export type ClientConsentView = z.infer<typeof ClientConsentView>;

/**
 * A template the client SHOULD sign before their next visit — an active template with no current
 * (non-expired) signed record. Customer-facing ONLY: the salonId/templateId here ARE the client's own
 * coordinates needed to address the self-serve sign route (POST /v1/client/consents/:salonId/:templateId/sign),
 * never another tenant's internal keys; no internal record/client/renter ids.
 */
export const PendingConsent = z
  .object({
    salonId: Uuid,
    salonName: z.string(),
    templateId: Uuid,
    kind: ConsentKind,
    title: z.string(),
    body: z.string(),
    /** Why it is pending: 'never' (never signed) or 'expired' (a dated record lapsed). */
    reason: z.enum(['never', 'expired']),
  })
  .meta({ id: 'PendingConsent' });
export type PendingConsent = z.infer<typeof PendingConsent>;

/**
 * The client self-serve sign body: a typed name + an explicit agree. signedVia is set to 'client_portal'
 * server-side; the templateId/salonId come from the path (re-verified against the claimed-salon RLS leg).
 */
export const SignConsent = z
  .object({
    signedName: NonEmptyName,
    agree: z.literal(true).describe('the explicit "I agree" attestation (must be true)'),
    answers: z.unknown().optional(),
  })
  .meta({ id: 'SignConsent' });
export type SignConsent = z.infer<typeof SignConsent>;
